import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref, onValue, query, orderByChild, limitToLast, off } from 'firebase/database';
import { database } from '../lib/firebase';

export interface UserNotification {
  id: string;
  title: string;
  content: string;
  category: string;
  actionUrl?: string;
  sentAt: string;
  sentBy: string;
  read: boolean;
  seen: boolean;
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMarkingAllAsReadRef = useRef(false);
  const markingAsReadIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    console.log('🔔 [useNotifications] Effect triggered');
    console.log('🔔 [useNotifications] User:', user?.uid, user?.email);
    console.log('🔔 [useNotifications] Database instance:', database ? 'Connected' : 'Not connected');
    
    if (!user?.uid) {
      console.log('🔔 [useNotifications] No user ID, clearing notifications');
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      // Set up real-time listener for user's notifications
      const notificationsPath = `users/${user.uid}/notifications`;
      console.log('🔔 [useNotifications] Setting up listener for path:', notificationsPath);
      
      const notificationsRef = ref(database, notificationsPath);
      const notificationsQuery = query(
        notificationsRef,
        orderByChild('sentAt'),
        limitToLast(50) // Get last 50 notifications
      );

      console.log('🔔 [useNotifications] Firebase query created, attaching onValue listener...');

      const unsubscribe = onValue(
        notificationsQuery,
        (snapshot) => {
          console.log('🔔 [useNotifications] Snapshot received');
          console.log('🔔 [useNotifications] Snapshot exists:', snapshot.exists());
          console.log('🔔 [useNotifications] isMarkingAllAsRead flag:', isMarkingAllAsReadRef.current);
          
          if (snapshot.exists()) {
            const data = snapshot.val();
            console.log('🔔 [useNotifications] Raw data from Firebase:', data);
            
            const notifList: UserNotification[] = Object.values(data);
            console.log('🔔 [useNotifications] Parsed notifications:', notifList.length, 'items');
            
            // Sort by sentAt descending (most recent first)
            notifList.sort((a, b) => {
              const dateA = new Date(a.sentAt).getTime();
              const dateB = new Date(b.sentAt).getTime();
              return dateB - dateA;
            });

            console.log('🔔 [useNotifications] Sorted notifications:', notifList.map(n => ({
              id: n.id,
              title: n.title,
              read: n.read,
              seen: n.seen,
              sentAt: n.sentAt
            })));

            setNotifications(notifList);
            
            // Calculate unread count from Firebase data, excluding notifications being marked as read
            const firebaseUnreadNotifications = notifList.filter(n => !n.read);
            const firebaseUnreadCount = firebaseUnreadNotifications.length;
            console.log('🔔 [useNotifications] Firebase unread count:', firebaseUnreadCount);
            console.log('🔔 [useNotifications] Currently marking as read:', Array.from(markingAsReadIdsRef.current));
            
            // Remove from markingAsReadIds any notifications that Firebase now shows as read
            // This auto-clears the tracking when Firebase catches up
            firebaseUnreadNotifications.forEach(notif => {
              if (markingAsReadIdsRef.current.has(notif.id)) {
                // Firebase still shows this as unread, but we're tracking it
                console.log('🔔 [useNotifications] Notification', notif.id, 'still pending in Firebase');
              }
            });
            
            // Check for notifications that are now read in Firebase and remove from tracking
            const nowReadInFirebase = notifList.filter(n => n.read);
            nowReadInFirebase.forEach(notif => {
              if (markingAsReadIdsRef.current.has(notif.id)) {
                console.log('🔔 [useNotifications] Notification', notif.id, 'confirmed read in Firebase, removing from tracking');
                markingAsReadIdsRef.current.delete(notif.id);
              }
            });
            
            // If we're marking all as read, keep the count at 0 regardless of Firebase data
            // This prevents race condition with optimistic update
            if (isMarkingAllAsReadRef.current) {
              console.log('🔔 [useNotifications] Mark all as read in progress - keeping unread count at 0');
              setUnreadCount(0);
              
              // If Firebase has caught up (all notifications are now read), clear the flag
              if (firebaseUnreadCount === 0) {
                console.log('🔔 [useNotifications] Firebase caught up - all notifications are read, clearing flag');
                isMarkingAllAsReadRef.current = false;
                markingAsReadIdsRef.current.clear(); // Clear individual tracking too
              }
            } else {
              // Normal operation - calculate unread count excluding notifications being marked as read
              const adjustedUnreadCount = firebaseUnreadNotifications.filter(
                n => !markingAsReadIdsRef.current.has(n.id)
              ).length;
              
              console.log('🔔 [useNotifications] Adjusted unread count (excluding pending):', adjustedUnreadCount);
              setUnreadCount(adjustedUnreadCount);
            }
          } else {
            console.log('🔔 [useNotifications] No notifications found in Firebase');
            setNotifications([]);
            setUnreadCount(0);
            
            // Clear the flags and tracking if they were set (user had no notifications to begin with)
            if (isMarkingAllAsReadRef.current) {
              console.log('🔔 [useNotifications] No notifications exist - clearing flag');
              isMarkingAllAsReadRef.current = false;
            }
            if (markingAsReadIdsRef.current.size > 0) {
              console.log('🔔 [useNotifications] No notifications exist - clearing tracking set');
              markingAsReadIdsRef.current.clear();
            }
          }
          setLoading(false);
          setError(null);
          console.log('🔔 [useNotifications] State updated successfully');
        },
        (error) => {
          console.error('❌ [useNotifications] Error listening to notifications:', error);
          console.error('❌ [useNotifications] Error code:', error.code);
          console.error('❌ [useNotifications] Error message:', error.message);
          setError(error.message);
          setLoading(false);
        }
      );

      console.log('🔔 [useNotifications] Listener attached successfully');

      // Cleanup on unmount
      return () => {
        console.log('🔔 [useNotifications] Cleaning up listener');
        const notificationsRef = ref(database, `users/${user.uid}/notifications`);
        off(notificationsRef);
      };
    } catch (err) {
      console.error('❌ [useNotifications] Error setting up notification listener:', err);
      console.error('❌ [useNotifications] Error details:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setLoading(false);
    }
  }, [user?.uid]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.uid) return;

    console.log('🔔 [markAsRead] Marking notification as read:', notificationId);
    
    // Add to tracking set BEFORE optimistic update
    markingAsReadIdsRef.current.add(notificationId);
    console.log('🔔 [markAsRead] Added to tracking set:', Array.from(markingAsReadIdsRef.current));

    // Optimistically update local state immediately
    setNotifications(prev => {
      const notification = prev.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.read;
      
      // Only decrement count if notification was actually unread
      if (wasUnread) {
        setUnreadCount(c => Math.max(0, c - 1));
      }
      
      return prev.map(n => n.id === notificationId ? { ...n, read: true } : n);
    });

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user.uid }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      console.log(`✅ Notification ${notificationId} marked as read on server`);
      // Note: The tracking ID will be automatically removed by the Firebase listener
      // once it confirms the notification is read
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
      // Remove from tracking on error so Firebase listener can restore correct state
      markingAsReadIdsRef.current.delete(notificationId);
      console.log('🔔 [markAsRead] Removed from tracking due to error');
      // Firebase listener will restore the correct state on error
    }
  }, [user?.uid]);

  // Mark notification as seen
  const markAsSeen = useCallback(async (notificationId: string) => {
    if (!user?.uid) return;

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/notifications/${notificationId}/seen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user.uid }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as seen');
      }

      console.log(`✅ Notification ${notificationId} marked as seen`);
    } catch (error) {
      console.error('❌ Error marking notification as seen:', error);
    }
  }, [user?.uid]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;

    console.log('🔔 [markAllAsRead] Starting mark all as read operation');
    
    // Set flag to prevent Firebase listener from overwriting our optimistic update
    isMarkingAllAsReadRef.current = true;
    
    // Clear individual tracking since we're marking all as read
    markingAsReadIdsRef.current.clear();
    console.log('🔔 [markAllAsRead] Cleared individual tracking');

    // Optimistically update local state immediately
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
    setUnreadCount(0);

    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const response = await fetch(`${serverUrl}/api/notifications/user/${user.uid}/read-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      console.log('✅ All notifications marked as read on server');
      
      // Note: The flag will be automatically cleared by the Firebase listener
      // once it confirms all notifications are read (see listener logic above)
      // This provides a more reliable clearing mechanism than a fixed timeout
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
      // Clear the flag on error so Firebase listener can restore correct state
      isMarkingAllAsReadRef.current = false;
      // Firebase listener will restore the correct state on error
    }
  }, [user?.uid]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAsSeen,
    markAllAsRead,
  };
}

